import { Button, Checkbox, Paper } from "@material-ui/core";
import { ArrowDownwardRounded } from "@material-ui/icons";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components/macro";
import { launchExperience } from "../store/selections/selectionsActions";
import { reportEvent } from "../tools/analytics";
import { PageViewTracker } from "./PageViewTracker";

const LegalDiv = styled.div`
	margin: 10px;
	width: 100%;
	flex: 1;
	display: flex;
	align-items: center;
	flex-direction: column;
	overflow-x: auto;
	overflow-x: hidden;
	text-align: center;
`;

const Title = styled.div`
	padding: 30px;
	font-size: 20px;
	line-height: 28px;
	text-align: center;
	font-weight: bold;
`;

const LegalStuff = styled.div`
	padding: 10px;
	padding-bottom: 30px;
	font-size: 14px;
	line-height: 28px;
	text-align: left;
`;

const ErrorDiv = styled.div`
	font-size: 14px;
	line-height: 28px;
	text-align: left;
	color: red;
`;

const CheckboxDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	align-self: flex-start;
	padding-left: 30px;
	transform: scale(1.5);
`;

const ContinueButton = styled(Button)`
	font-size: 24px;
	border-radius: 100px;
	padding: 15px 45px;
	margin: 10px;
	margin-bottom: 50px;
	text-transform: none;
	font-weight: normal;
	background: #012169;
	min-width: 75%;
`;

const ScrollHint = styled(Paper)`
	position: fixed;
	bottom: 0px;
	width: 100%;
	height: 100px;
	padding-top: 25px;
	pointer-events: none;
`;

const DownArrow = styled(ArrowDownwardRounded)`
	transform: scale(2);
`;

function Legal(): ReactElement {
	const dispatch = useDispatch();
	const [checked, setChecked] = useState(false);
	const [showError, setShowError] = useState(false);
	const div = useRef<HTMLDivElement>(null);
	const [showHint, setShowHint] = useState(true);

	const toggleAccept = () => {
		const newChecked = !checked;
		setChecked(newChecked);
		reportEvent("legalAccepted", { checked: newChecked });
	};

	useEffect(() => {
		if (div.current) {
			// Determine if we've scrolled to the bottom of the page
			const getAtBottom = () => {
				if (div.current) {
					const { height } = div.current.getBoundingClientRect();
					const { scrollTop, scrollHeight } = div.current;
					return scrollTop + height >= scrollHeight - 100;
				}
				return true;
			};

			// If are already at the bottom (large monitor) hide the hint
			if (getAtBottom()) {
				setShowHint(false);
			} else {
				// Otherwise bind to scroll
				div.current.onscroll = () => {
					const atBottom = getAtBottom();
					if (atBottom) setShowHint(false);
				};
			}
		}
	}, [div]);

	const submit = () => {
		if (checked) dispatch(launchExperience());
		else setShowError(true);
	};

	return (
		<LegalDiv ref={div}>
			<Title>
				Research Consent/
				<wbr />
				Release/
				<wbr />
				Confidentiality Form <br />
				(“Non-Disclosure Agreement”)
			</Title>
			<LegalStuff>
				I agree to participate in a survey, web application, and online experience (hereinafter the “
				<b>Study</b>”) conducted by Lowe&apos;s Companies, Inc. (“<b>Lowe&apos;s</b>”) and Batterii LLC dba
				Vurvey (“<b>Vurvey</b>”). <br />
				<br />
				In consideration of being able to participate in the Study, and in consideration of my receipt of the
				compensation paid to me by Vurvey and other good and valuable consideration, the receipt and sufficiency
				of which are hereby acknowledged, I hereby understand and agree to the following: <br />
				<br />
				1. <u>Lowe&apos;s Ownership</u>. Lowe&apos;s shall be the sole and exclusive property owner of all
				suggestions, ideas, results, answers, notes, audio recordings, video recordings, feedback or any other
				information offered during the course of my participation in the Study (collectively, the “
				<b>Feedback</b>”). I shall not claim any ownership interest in the Feedback. To the extent I am deemed
				under any law to retain any rights in the Feedback, I hereby grant to Lowe&apos;s, its respective legal
				representatives, agents, assigns and those acting with its authority and/or permission, the absolute,
				irrevocable, unrestricted, perpetual, worldwide, transferable, royalty-free license, with the right to
				sublicense, to use for commercial purposes, reproduce, modify, edit, alter, copy, copyright, create
				derivative works based on, store on its servers, display, perform, promote, publish, project, exhibit,
				produce, print, market, distribute, broadcast, offer for sale and sell, permit the online use of or
				otherwise use all Feedback. I agree that Lowe&apos;s may use the Feedback for any lawful purpose,
				including, but not limited to, marketing, advertising, publicity, illustration, training and web
				content. In regard to the rights I grant and assign to Lowe&apos;s above, I hereby waive any right that
				I may have to inspect or approve the Feedback, the use of the Feedback, or any other matter related
				thereto. <br />
				<br />
				2. <u>Confidentiality</u>. I recognize, acknowledge and agree that any information, including any
				business information of any kind, communicated by Lowe&apos;s to me related to this Non-Disclosure
				Agreement, the Study, or the rights granted hereunder, shall be considered confidential and proprietary
				information, and shall not be disclosed by me to any person, entity, or party outside of the Study.
				Accordingly, I agree to not publicly comment on or post materials shown, generated or discussed during
				the Study on any internet blogs, social networking, media sharing sites (e.g., Facebook, Instagram,
				Twitter, Snapchat, TikTok, LinkedIn, YouTube, etc.) or otherwise. <br />
				<br />
				3. <u>Release</u>. I hereby release, discharge and agree to save and hold harmless Lowe&apos;s, and its
				legal representatives, officers, employees, agents, successors in interest, assigns, and all those
				acting with its authority and/or permission, from and against any and all claims, actions, causes of
				action, and/or liability arising out of this Study, including without limitation, any claims for libel,
				slander, invasion of privacy, right of publicity, defamation, physical or psychological injury,
				economical or emotional loss, passing off, misappropriation of personality, trademark infringement,
				copyright infringement, or moral rights. <br />
				<br />
				4. <u>Remedies</u>. I expressly recognize, acknowledge and agree that any breach of this Non-Disclosure
				Agreement is likely to result in irreparable harm to Lowe&apos;s. In the event of any actual or
				threatened breach of this Non-Disclosure Agreement, I agree that Lowe&apos;s shall be entitled, in
				addition to all other remedies available under law, to obtain specific performance of this
				Non-Disclosure Agreement, and to obtain injunctive relief against ongoing or contemplated activities in
				violation of this Non-Disclosure Agreement against me, any of my representatives, servants, employees,
				parents, subsidiaries, affiliates and any and all agents or other persons acting in active concert or
				participation with me. <br />
				<br />
				5. <u>Governing Law</u>. The validity, interpretation, construction and performance of this
				Non-Disclosure Agreement, and all acts and transactions pursuant hereto and the rights and obligations
				of the parties hereto shall be governed, construed and interpreted in accordance with the laws of the
				state of North Carolina. I agree to sole jurisdiction and venue in any state or federal court located in
				Statesville, North Carolina. <br />
				<br />
				6. <u>Advice of Counsel</u>. I ACKNOWLEDGE THAT, IN EXECUTING THIS NON-DISCLOSURE AGREEMENT, I HAVE HAD
				THE OPPORTUNITY TO SEEK THE ADVICE OF INDEPENDENT LEGAL COUNSEL, AND I HAVE READ AND UNDERSTOOD ALL OF
				THE TERMS AND PROVISIONS OF THIS NON-DISCLOSURE AGREEMENT. THIS NON-DISCLOSURE AGREEMENT SHALL NOT BE
				CONSTRUED AGAINST ANY PARTY BY REASON OF THE DRAFTING OR PREPARATION HEREOF. <br />
				<br />
				This Non-Disclosure Agreement shall be binding upon me, my heirs, personal representatives, executors
				and administrators and legal representatives. I have read the above authorization, release and
				agreement, prior to its execution, and am fully familiar with the contents thereof.
			</LegalStuff>
			<CheckboxDiv onClick={toggleAccept}>
				<Checkbox color="primary" checked={checked} />
				<div>I accept</div>
			</CheckboxDiv>
			<ErrorDiv style={{ visibility: showError ? "visible" : "hidden" }}>
				Please check accept box before submitting
			</ErrorDiv>
			<ContinueButton color="primary" variant="contained" onClick={submit}>
				Submit
			</ContinueButton>
			<ScrollHint style={{ display: showHint ? "initial" : "none" }}>
				<DownArrow />
			</ScrollHint>
			<PageViewTracker pageName={"LegalDisclaimer"} />
		</LegalDiv>
	);
}

export default Legal;
